import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button, Card, Col, Form, Row, Spinner, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import AddTeamModal from '../teams/AddTeamModal';
import { buildSeasonOptions, fetchActiveSeasons, getSeasonOptionLabel } from '../utils/activeSeasonUtil';

function ManagerTeamListPage() {
    const [teams, setTeams] = useState([]);
    const [seasonOptions, setSeasonOptions] = useState([]);
    const [selectedSeason, setSelectedSeason] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);

    const fetchTeams = useCallback(async (seasonId) => {
        if (!seasonId) {
            setTeams([]);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const response = await axios.get('/api/teams/', {
                params: seasonId === 'all' ? {} : { season: seasonId },
                withCredentials: true,
            });
            setTeams(response.data.results || response.data);
            setError(null);
        } catch (fetchError) {
            console.error('Error fetching teams:', fetchError);
            setError('Failed to fetch teams');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        let isMounted = true;

        const loadSeasons = async () => {
            try {
                setLoading(true);
                const [activeSeasons, leaguesResponse] = await Promise.all([
                    fetchActiveSeasons(),
                    axios.get('/api/leagues/leagues/', { withCredentials: true }),
                ]);
                if (!isMounted) {
                    return;
                }

                const allSeasonOptions = buildSeasonOptions(activeSeasons, leaguesResponse.data);
                setSeasonOptions(allSeasonOptions);
                if (activeSeasons.length > 0) {
                    const defaultSeasonId = activeSeasons[0].id.toString();
                    setSelectedSeason(defaultSeasonId);
                } else {
                    setTeams([]);
                    setError(null);
                    setSelectedSeason('all');
                    setLoading(false);
                }
            } catch (loadError) {
                if (!isMounted) {
                    return;
                }
                console.error('Error fetching active seasons:', loadError);
                setError('Failed to fetch active seasons');
                setLoading(false);
            }
        };

        loadSeasons();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (!selectedSeason) {
            return;
        }
        fetchTeams(selectedSeason);
    }, [fetchTeams, selectedSeason]);

    const handleTeamAdded = async () => {
        setShowCreateTeamModal(false);
        await fetchTeams(selectedSeason);
    };

    if (loading) {
        return (
            <Card>
                <Card.Body>
                    <div className="text-center">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                    </div>
                </Card.Body>
            </Card>
        );
    }

    if (error) {
        return (
            <Card>
                <Card.Body>
                    <Alert variant="danger" className="mb-0">
                        {error}
                    </Alert>
                </Card.Body>
            </Card>
        );
    }

    return (
        <Card>
            <Card.Header className="border-0 pb-0">
                <h5 className="card-title">Teams</h5>
                <p className="mb-0">View and manage teams for the selected season.</p>
            </Card.Header>
            <Card.Body>
                <Row className="align-items-end mb-4 g-3">
                    <Col md={6} lg={4}>
                        <Form.Group controlId="manager-team-season-select">
                            <Form.Label>Season</Form.Label>
                            <Form.Select
                                value={selectedSeason}
                                onChange={(event) => setSelectedSeason(event.target.value)}
                            >
                                <option value="all">All Seasons</option>
                                {seasonOptions.map((season) => (
                                    <option key={season.id} value={season.id}>
                                        {getSeasonOptionLabel(season)}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col className="d-flex justify-content-md-end">
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => setShowCreateTeamModal(true)}
                            disabled={!selectedSeason || selectedSeason === 'all'}
                        >
                            Add Team
                        </Button>
                    </Col>
                </Row>

                {teams.length === 0 ? (
                    <div className="text-center text-muted py-4">
                        No teams found for this season.
                    </div>
                ) : (
                    <ul className="list-group">
                        {teams.map((team) => (
                            <li
                                key={team.id}
                                className="list-group-item"
                            >
                                <div className="d-flex align-items-start mb-2 pb-2">
                                    <div className="me-3" style={{ width: '40px', flexShrink: 0 }}>
                                        {team.league_name && (
                                            <div className="text-center mb-1">
                                                <span
                                                    className="badge rounded-pill text-white"
                                                    style={{
                                                        fontSize: '0.65rem',
                                                        backgroundColor: team.color || '#6c757d',
                                                    }}
                                                >
                                                    {team.league_name}
                                                </span>
                                            </div>
                                        )}
                                        <div style={{ width: '40px', height: '40px' }}>
                                            <img
                                                src={team.logo || '/static/images/kb_static/lemont_rec_leagues_circle.png'}
                                                alt={`${team.name} logo`}
                                                className="img-fluid rounded-circle"
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    border: '1px solid #ddd',
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex-grow-1 p-1">
                                        <div className="mb-1 d-flex">
                                            <strong>
                                                <Link
                                                    to={`/league-manager/teams/${team.id}/`}
                                                    className="text-decoration-none"
                                                >
                                                    {team.name}
                                                </Link>
                                            </strong>
                                        </div>
                                        <div className="small text-muted">
                                            {team.captain_name ? (
                                                <div>Captain: {team.captain_name}</div>
                                            ) : (
                                                <div>No captain assigned</div>
                                            )}
                                            {team.a_cap_name ? (
                                                <div>A-Captain: {team.a_cap_name}</div>
                                            ) : (
                                                <div>No assistant captain assigned</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="d-flex justify-content-between gap-2">
                                    <span className="badge bg-success rounded-pill" style={{ fontSize: '0.75rem' }}>
                                        {team.player_count || 0} {team.player_count === 1 ? 'Player' : 'Players'}
                                    </span>
                                    <span className="badge bg-primary rounded-pill" style={{ fontSize: '0.75rem' }}>
                                        {team.total_games} Games
                                    </span>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </Card.Body>
            <AddTeamModal
                show={showCreateTeamModal}
                handleClose={() => setShowCreateTeamModal(false)}
                onTeamAdded={handleTeamAdded}
                requireCaptain={false}
            />
        </Card>
    );
}

export default ManagerTeamListPage;
